import {
  BookOpen,
  Code2,
  ExternalLink,
  FileText,
  FolderKanban,
  FolderOpen,
  Globe2,
  LayoutDashboard,
  PanelsTopLeft,
  Table2,
  Workflow,
  X,
} from "lucide-react";
import { motion } from "motion/react";
import { memo } from "react";
import { useTranslation } from "react-i18next";

import { CodePreviewSidebarView } from "@/components/views/code-preview-sidebar-view";
import { BrowserSessionView } from "@/components/views/browser-session-view";
import { SqliteTableView } from "@/components/lazy-heavy-components";
import { FileBrowserView } from "@/components/views/file-browser-view";
import type { BrowserMount } from "@/components/views/file-browser-scope";
import { SingleFileView } from "@/components/views/single-file-view";
import { DocxView } from "@/components/lazy-heavy-components";
import { WikiView } from "@/components/views/wiki-view";
import { WorkflowView } from "@/components/lazy-heavy-components";
import { ProjectKanbanView } from "@/components/views/project-kanban-view";
import { VisualEditorView } from "@/components/lazy-heavy-components";
import { DashboardView } from "@/components/views/dashboard-view";
import { ScheduleRunsView } from "@/components/lazy-heavy-components";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import type { AsidePanelResize } from "@/hooks/use-aside-view";
import { useIsMobile } from "@/hooks/use-media-query";
import { isSourceFileName } from "@/lib/file-links";
import type { AccessTokenGetter } from "@/lib/utils";
import {
  isBrowserSessionViewConfig,
  isKanbanBoardViewConfig,
  isSingleFileViewConfig,
  isWorkflowViewConfig,
  viewRefKey,
  type ViewRef,
  type ViewSummaryConfig,
  type ViewType,
} from "@/api/views";

export interface AgentViewPanelProps {
  viewRef: ViewRef;
  title: string;
  viewType: ViewType;
  availability?: "available" | "unavailable";
  initialPath?: string | null;
  viewConfig?: ViewSummaryConfig;
  browserMounts?: BrowserMount[];
  onBrowserMountChange?: (mount: BrowserMount) => void;
  browserMountHintInvalid?: boolean;
  /** A newer pinned-editor contract was advertised by the summary, but the
   * connected server does not advertise a compatible capability. */
  pinnedFileEditingUnsupported?: boolean;
  getToken: AccessTokenGetter;
  refreshKey?: number;
  onPublish?: (prompt: string) => void;
  publishDisabled?: boolean;
  /** Optional server-backed action for a viewable file in a browser view. */
  onPinFile?: (path: string) => Promise<void> | void;
  isFilePinned?: (path: string) => boolean;
  pinningFilePath?: string | null;
  onOpenScheduleRun?: (sessionId: string) => void;
  onOpenWorkflowRun?: (taskId: string) => void;
  onClose: () => void;
  /** Shared desktop resize controller from the active aside manager. */
  resize: AsidePanelResize;
  /** Fill the parent content surface instead of rendering as a desktop aside. */
  fullscreen?: boolean;
  /** Keep an inactive dashboard panel mounted without displaying it. */
  active?: boolean;
}

/**
 * Responsive in-chat surface for a configured view.
 *
 * Desktop uses the same flex-sibling/sidebar pattern as the markdown viewer;
 * mobile uses a full-screen dialog. The standalone route remains available
 * through the pop-out affordance in the panel header. This is memoized because
 * the parent also owns the independently-polled session catalog.
 */
export const AgentViewPanel = memo(function AgentViewPanel({
  viewRef,
  title,
  viewType,
  browserMounts,
  onBrowserMountChange,
  browserMountHintInvalid,
  availability = "available",
  initialPath,
  viewConfig,
  pinnedFileEditingUnsupported = false,
  getToken,
  refreshKey,
  onPublish,
  publishDisabled,
  onPinFile,
  isFilePinned,
  pinningFilePath,
  onClose,
  onOpenScheduleRun,
  onOpenWorkflowRun,
  resize,
  fullscreen = false,
  active = true,
}: AgentViewPanelProps) {
  const { t } = useTranslation("chat");
  const isMobile = useIsMobile();
  const isWiki = viewType === "wiki";
  const isFileBrowser = viewType === "file_browser";
  const isSingleFile = viewType === "single_file" || (
    viewRef.scope === "conversation"
    && isFileBrowser
    && (isMarkdownFileName(initialPath) || isSourceFileName(initialPath) || /\.html?$/i.test(initialPath ?? ""))
  );
  const isDocx = viewType === "docx";
  const isCodePreview = viewType === "code_preview_sidebar";
  const isVisualEditor = viewType === "visual_editor";
  const isDashboard = viewType === "dashboard";
  const isWorkflow = viewType === "workflow";
  const isKanbanBoard = viewType === "kanban_board";
  const isBrowserSession = viewType === "browser_session";
  const workflowConfig = isWorkflowViewConfig(viewConfig) ? viewConfig : undefined;
  const kanbanBoardConfig = isKanbanBoardViewConfig(viewConfig) ? viewConfig : undefined;
  const browserSessionConfig = isBrowserSessionViewConfig(viewConfig)
    ? viewConfig
    : undefined;
  const openSingleFileInEdit = isSingleFile
    && isSingleFileViewConfig(viewConfig)
    && viewConfig.open_in_edit === true;
  const hasSingleFileSurface = availability !== "unavailable" && isSingleFile;
  const hasIntegratedHeaderSurface =
    hasSingleFileSurface
    || (availability !== "unavailable" && isDocx)
    || (availability !== "unavailable" && isCodePreview)
    || (availability !== "unavailable" && isBrowserSession);
  const headerViewType = isSingleFile ? "single_file" : viewType;
  const openInNewTab = () => {
    const href = viewRef.scope === "conversation"
      ? `/session/${encodeURIComponent(viewRef.sessionId)}/views/${encodeURIComponent(viewRef.viewId)}`
      : `/agent/${encodeURIComponent(viewRef.agentId)}/views/${encodeURIComponent(viewRef.viewId)}`;
    window.open(
      href,
      "_blank",
      "noopener",
    );
  };

  const content = availability === "unavailable" ? (
    <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
      {t("views.unavailable")}
    </div>
  ) : isWiki ? (
    <WikiView
      viewRef={viewRef}
      initialPath={initialPath}
      getToken={getToken}
      variant="panel"
    />
  ) : isWorkflow ? (
    <WorkflowView
      fileName={workflowConfig?.file_name}
      agentId={viewRef.agentId}
      getToken={getToken}
      onOpenRun={onOpenWorkflowRun}
    />
  ) : isKanbanBoard ? (
    <ProjectKanbanView
      projectId={kanbanBoardConfig?.project_id}
      getToken={getToken}
    />
  ) : isBrowserSession ? (
    <BrowserSessionView
      key={viewRefKey(viewRef)}
      viewRef={viewRef}
      getToken={getToken}
      title={title}
      config={browserSessionConfig}
      onClose={onClose}
    />
  ) : isSingleFile ? (
    <SingleFileView
      key={viewRefKey(viewRef)}
      viewRef={viewRef}
      getToken={getToken}
      openInEdit={openSingleFileInEdit}
      fallbackTitle={title}
      onClose={onClose}
      onOpenInNewTab={openInNewTab}
      compact={isMobile}
      pinnedFileEditingUnsupported={pinnedFileEditingUnsupported}
    />
  ) : isDocx ? (
    <DocxView
      key={viewRefKey(viewRef)}
      viewRef={viewRef}
      getToken={getToken}
      fallbackTitle={title}
      compact={isMobile}
      onClose={onClose}
      onOpenInNewTab={openInNewTab}
    />
  ) : isFileBrowser ? (
    <FileBrowserView
      key={viewRefKey(viewRef)}
      viewRef={viewRef}
      initialPath={initialPath}
      getToken={getToken}
      variant="panel"
      mounts={browserMounts}
      onMountChange={onBrowserMountChange}
      mountHintInvalid={browserMountHintInvalid}
      onPinFile={onPinFile}
      isFilePinned={isFilePinned}
      pinningFilePath={pinningFilePath}
      pinnedFileEditingUnsupported={pinnedFileEditingUnsupported}
    />
  ) : isCodePreview ? (
    <CodePreviewSidebarView
      key={viewRefKey(viewRef)}
      viewRef={viewRef}
      getToken={getToken}
      refreshKey={refreshKey}
      onPublish={viewRef.scope === "agent" ? onPublish : undefined}
      publishDisabled={viewRef.scope !== "agent" || publishDisabled}
      shareAgentUrl={viewRef.scope === "agent"}
      onClose={onClose}
      onOpenInNewTab={openInNewTab}
      compact={isMobile}
    />
  ) : isVisualEditor ? (
    <VisualEditorView
      key={viewRefKey(viewRef)}
      viewRef={viewRef}
      getToken={getToken}
    />
  ) : isDashboard ? (
    <DashboardView
      key={viewRefKey(viewRef)}
      viewRef={viewRef}
      getToken={getToken}
    />
  ) : viewType === "schedule_runs" ? (
    <ScheduleRunsView viewRef={viewRef} getToken={getToken} onOpenSession={onOpenScheduleRun} />
  ) : viewType === "sqlite_table" ? (
    <SqliteTableView viewRef={viewRef} getToken={getToken} />
  ) : (
    <div className="flex h-full items-center justify-center p-6 text-center text-sm text-muted-foreground">
      {t("views.typeUnavailable")}
    </div>
  );
  const contentClassName =
    isWiki || isFileBrowser || isSingleFile || isDocx || isCodePreview || isVisualEditor || isDashboard || isWorkflow || isKanbanBoard || isBrowserSession
      ? "min-h-0 flex-1 overflow-hidden"
      : "min-h-0 flex-1 overflow-hidden p-4";

  if (isMobile) {
    return (
      <Dialog open={active} onOpenChange={(open) => { if (!open && active) onClose(); }}>
        <DialogContent
          className="flex h-full max-h-full w-full max-w-full flex-col gap-0 overflow-hidden rounded-none p-0"
          hideCloseButton
          keepMounted={isDashboard}
          hidden={!active}
        >
          <DialogTitle className="sr-only">{title}</DialogTitle>
          <div className="flex min-h-0 flex-1 flex-col">
            {!hasIntegratedHeaderSurface && (
              <ViewPanelHeader title={title} viewType={headerViewType} onClose={onClose} onOpenInNewTab={openInNewTab} />
            )}
            <div className={contentClassName}>{content}</div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (fullscreen) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-background"
        style={{ display: active ? undefined : "none" }}
        data-testid={active ? "agent-view-panel" : undefined}
        data-layout="fullscreen"
      >
        {!hasIntegratedHeaderSurface && (
          <ViewPanelHeader title={title} viewType={headerViewType} onClose={onClose} onOpenInNewTab={openInNewTab} />
        )}
        <div className={contentClassName}>{content}</div>
      </motion.div>
    );
  }

  return (
    <motion.aside
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ type: "spring", damping: 30, stiffness: 300 }}
      className="relative flex h-full min-w-0 shrink-0 flex-col overflow-hidden border-l bg-background"
      style={{ width: resize.width, display: active ? undefined : "none" }}
      data-testid={active ? "agent-view-panel" : undefined}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t("views.resizePanel")}
        onPointerDown={resize.onPointerDown}
        onPointerMove={resize.onPointerMove}
        onPointerUp={resize.onPointerUp}
        onDoubleClick={resize.onDoubleClick}
        className="absolute -left-[3px] top-0 z-20 h-full w-1.5 touch-none select-none cursor-col-resize transition-colors hover:bg-primary/30 active:bg-primary/40"
      />
      {!hasIntegratedHeaderSurface && (
        <ViewPanelHeader title={title} viewType={headerViewType} onClose={onClose} onOpenInNewTab={openInNewTab} />
      )}
      <div className={contentClassName}>{content}</div>
    </motion.aside>
  );
});

function ViewPanelHeader({
  title,
  viewType,
  onClose,
  onOpenInNewTab,
}: {
  title: string;
  viewType: ViewType;
  onClose: () => void;
  onOpenInNewTab: () => void;
}) {
  const { t } = useTranslation();
  const Icon = viewType === "wiki"
    ? BookOpen
    : viewType === "workflow"
      ? Workflow
    : viewType === "kanban_board"
      ? FolderKanban
    : viewType === "file_browser"
      ? FolderOpen
      : viewType === "single_file" || viewType === "docx"
        ? FileText
      : viewType === "code_preview_sidebar"
        ? Code2
        : viewType === "visual_editor"
          ? PanelsTopLeft
        : viewType === "dashboard"
          ? LayoutDashboard
        : viewType === "browser_session"
          ? Globe2
        : Table2;
  return (
    <header
      className="flex min-h-[53px] shrink-0 items-center justify-between gap-2 border-b px-3 py-2.5"
      data-testid="view-panel-header"
    >
      <div className="flex min-w-0 items-center gap-2">
        <Icon className="size-4 shrink-0 text-muted-foreground" />
        <span className="truncate text-sm font-semibold" title={title}>{title}</span>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onOpenInNewTab}
          aria-label={t("viewer.openInNewWindow")}
        >
          <ExternalLink className="size-3.5" />
        </Button>
        <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label={t("viewer.closeViewer")}>
          <X className="size-4" />
        </Button>
      </div>
    </header>
  );
}

function isMarkdownFileName(path: string | null | undefined): boolean {
  return Boolean(path && /\.(md|markdown|mdown|mkd)$/i.test(path));
}
